import { Fragment, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import { ArrowDown, ArrowUp, ArrowUpDown, Check, ChevronLeft, Equal } from "lucide-react";
import * as api from "../lib/api.ts";
import { useSessionHue } from "../hooks/useHue.ts";
import { notify, notifyError } from "../lib/notify.ts";
import { ConnectionPill } from "../components/Chrome.tsx";
import { PickSession } from "./PickSession.tsx";
import { CancelSessionDialog } from "../components/CancelSessionDialog.tsx";
import { SessionEffort } from "../components/SessionEffort.tsx";
import { SessionCoach } from "../components/SessionCoach.tsx";
import { SessionRecap } from "../components/SessionRecap.tsx";
import { SessionWearableCard } from "../components/SessionWearableCard.tsx";
import { ReorderList } from "../components/ReorderList.tsx";
import { InsightLabel, Insights } from "../components/Insight.tsx";
import { exerciseTitle, withTransition } from "../lib/transition.ts";
import { groupRows, isStepDone, roundsOf } from "../lib/circuit.ts";
import { blocksOf } from "../lib/reorder.ts";
import { pairedWithPrevious } from "../lib/superset.ts";
import { withPending } from "../lib/pending.ts";
import { usePendingQueue } from "../hooks/usePendingQueue.ts";
import { compareToPrevious, formatGap, progressCount, type CompareCell, type Comparison } from "../lib/compare.ts";
import { SideMark } from "../components/SideMark.tsx";
import { CoachedBadge } from "../components/Avatar.tsx";
import { sessionBack } from "../lib/back.ts";
import {
  breakableName,
  cancelSummary,
  describe,
  doneCount as setsDoneOn,
  format,
  formatDate,
  isComplete,
  ordinal,
  SIDES,
} from "../lib/session.ts";

/**
 * The session as a checklist of movements.
 *
 * Entering weights happens one movement at a time on its own screen, so this
 * view has one job: show what is left to do and get out of the way. Tapping a
 * movement opens it.
 */
export function SessionScreen() {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const client = useQueryClient();

  const current = useQuery({
    queryKey: ["session", "current"],
    queryFn: api.getCurrentSession,
    enabled: !params.id,
  });

  const sessionId = params.id ? Number(params.id) : (current.data?.session?.id ?? null);

  const detail = useQuery({
    queryKey: ["session", sessionId],
    queryFn: () => api.getSessionDetail(sessionId!),
    enabled: sessionId !== null,
  });
  const pending = usePendingQueue();

  const block = useQuery({ queryKey: ["block"], queryFn: api.getCurrentBlock });
  const account = useQuery({ queryKey: ["account"], queryFn: api.getAccount });

  // A test run or a session opened by mistake: it should leave no trace.
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const cancel = useMutation({
    mutationFn: api.cancelSession,
    onSuccess: async () => {
      setConfirmingCancel(false);
      notify("Séance annulée.");
      await client.invalidateQueries();
      navigate("/choisir", { replace: true });
    },
    onError: async (error) => {
      setConfirmingCancel(false);
      notifyError("Séance non annulée", error);
      // A refusal may come from a set that landed meanwhile: show it.
      await client.invalidateQueries({ queryKey: ["session", sessionId] });
    },
  });

  // Finishing is what makes a session count for its week. Until 27/09 there
  // was no way to do it from the phone at all.
  const finish = useMutation({
    mutationFn: api.finishSession,
    onSuccess: async () => {
      navigator.vibrate?.(25);
      notify("Séance enregistrée.");
      await client.invalidateQueries();
    },
    onError: (error) => notifyError("Séance non terminée", error),
  });

  const [reordering, setReordering] = useState(false);
  const reorder = useMutation({
    mutationFn: (sessionExerciseIds: number[]) => api.reorderSession(sessionId!, sessionExerciseIds),
    onSuccess: async (result) => {
      setReordering(false);
      if (result.queued) notify("Hors ligne : gardé, envoyé au retour du réseau.");
      await client.invalidateQueries({ queryKey: ["session", sessionId] });
    },
    onError: async (error) => {
      notifyError("Ordre non enregistré", error);
      // Refused when a movement was added meanwhile: show the session as it is.
      await client.invalidateQueries({ queryKey: ["session", sessionId] });
      setReordering(false);
    },
  });

  const reopen = useMutation({
    mutationFn: api.reopenSession,
    onSuccess: () => client.invalidateQueries(),
    onError: (error) => notifyError("Séance non rouverte", error),
  });

  // The coach asks for the effort half an hour after the session: the
  // summary waits for it, and is written as soon as it is scored.
  const summarize = useMutation({
    mutationFn: api.writeSessionSummary,
    onSuccess: (summary) =>
      summary === null ? undefined : client.invalidateQueries({ queryKey: ["session", sessionId] }),
    onError: (error) => notifyError("Résumé non écrit", error),
  });

  useSessionHue(detail.data?.session.templateId);

  // Nothing open: the cycle has three candidates, so ask which one.
  if (sessionId === null && !current.isLoading) return <PickSession />;
  if (!detail.data) return <div className="page py-20 text-ink-mute">Chargement…</div>;

  const { session, exercises } = withPending(detail.data, pending);
  const doneCount = exercises.filter((entry) => isComplete(entry)).length;
  const nextIndex = exercises.findIndex((entry) => !isComplete(entry));

  const summary = cancelSummary(exercises);

  // Read against the time before once the session is over: what gets shown
  // to the coach after a session done alone. Mid-session, the list stays lean.
  const finished = session.status === "done";
  const athlete = account.data?.user.role === "athlete";
  const comparisons = new Map(exercises.map((entry) => [entry.id, finished ? compareToPrevious(entry) : null]));
  const progress = progressCount([...comparisons.values()]);

  const open = (index: number) =>
    withTransition(() => navigate(`/seance/${session.id}/exercice/${index}`, { state: location.state }));
  const back = sessionBack(location.state, session.status);

  return (
    <div className="page max-md:pt-4">
      <header className="max-md:safe-top">
        {/*
          The way back. The bottom tab "Séance" resolves to whatever session is
          open, so from inside one it goes nowhere — without this link a phone
          had no route back to the three candidates except scrolling past every
          movement. It leads where the session was opened from.
        */}
        <Link
          to={back.to}
          className="hit mb-2 -ml-1 inline-flex items-center gap-1 py-1 text-[0.82rem] text-ink-mute hover:text-ink active:text-ink"
        >
          <ChevronLeft size={16} aria-hidden />
          {back.label}
        </Link>

        {/* When and in which block, for a session looked back on. Mid-session
            it is today, this week: the home screen says so already. */}
        <div className="flex items-center justify-between gap-3">
          {finished ? (
            <p className="label">
              {block.data?.block && (
                <>
                  {block.data.block.name} · semaine {block.data.block.weekIndex}/{block.data.block.weeks} ·{" "}
                </>
              )}
              {formatDate(session.date)}
            </p>
          ) : (
            <span />
          )}
          <ConnectionPill />
        </div>

        <h1 className="display mt-2 ink-hue text-[2.4rem] font-bold md:text-[3.4rem]">{breakableName(session.name)}</h1>
        {session.withCoach && <CoachedBadge className="mt-2" />}
      </header>

      <div className="mt-6 flex flex-col gap-6 lg:grid lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start lg:gap-10">
        {/* On a desktop this column stays in view while the list scrolls. */}
        <aside className="space-y-3 empty:hidden lg:sticky lg:top-10 lg:col-start-2 lg:row-start-1">
          {session.status === "done" && (
            <div className="card px-5 py-4">
              <p className="label flex items-center gap-1.5 text-ink-soft">
                <Check size={14} strokeWidth={3} aria-hidden /> Séance terminée
              </p>
              <p className="wide mt-1 text-[1.1rem] font-bold">Comptée pour la semaine.</p>
              <button
                type="button"
                disabled={reopen.isPending}
                onClick={() => reopen.mutate(session.id)}
                className="mt-2 text-[0.82rem] text-ink-mute hover:text-ink-soft active:text-ink disabled:opacity-50"
              >
                Rouvrir la séance
              </button>
              <SessionEffort
                key={session.id}
                sessionId={session.id}
                effort={session.perceivedEffort}
                onScored={
                  athlete && session.summary === null && !summarize.isPending
                    ? () => summarize.mutate(session.id)
                    : undefined
                }
              />
              <SessionCoach key={`coach-${session.id}`} sessionId={session.id} withCoach={session.withCoach} />
              <SessionRecap session={session} entries={exercises} />
            </div>
          )}

          {/* The wearable's read of it, once it is over: mid-session it would only distract. */}
          {finished && <SessionWearableCard wearable={detail.data.wearable} />}
          {finished && <Insights scope={`session/${session.id}`} />}

          {/* Written by Claude once the effort is scored, or from the API: read it
              here, not in a terminal. For the athlete: the coach makes their own
              reading. While it is written, a still line — no spinner to keep the
              screen busy. */}
          {athlete && (session.summary !== null || summarize.isPending) && (
            <div className="card px-5 py-4" aria-busy={session.summary === null}>
              <InsightLabel>Résumé de séance</InsightLabel>
              <p className="mt-1.5 text-[0.92rem] leading-relaxed whitespace-pre-line text-ink-soft">
                {session.summary ?? <span className="text-ink-mute">Analyse en cours…</span>}
              </p>
            </div>
          )}

          {/* The next movement is lit in the list rather than repeated above
              it: a card for the line right under it said it twice. */}
          {session.status === "done" || nextIndex >= 0 ? null : (
            <button
              type="button"
              disabled={finish.isPending}
              onClick={() => finish.mutate(session.id)}
              className="slab flex w-full items-center gap-3 rounded-card px-5 py-4 text-left disabled:opacity-60"
            >
              <span className="min-w-0 flex-1">
                <span className="block text-[0.66rem] font-bold tracking-[0.12em] uppercase opacity-65">
                  Tous les mouvements sont faits
                </span>
                <span className="wide mt-0.5 block text-[1.2rem] font-bold">
                  {finish.isPending ? "Enregistrement…" : "Terminer la séance"}
                </span>
              </span>
              <Check size={26} strokeWidth={2.5} className="shrink-0" aria-hidden />
            </button>
          )}

          {finished && progress.up + progress.same + progress.down > 0 && (
            <div className="card px-5 py-3.5">
              <p className="label">Par rapport à la fois d'avant</p>
              <dl className="mt-2 grid grid-cols-3 text-center">
                <Trend value={progress.up} label="en hausse" className="text-gain" />
                <Trend value={progress.same} label="stables" className="text-ink-soft" />
                <Trend value={progress.down} label="en baisse" className="text-iron" />
              </dl>
            </div>
          )}
        </aside>

        <div className="lg:col-start-1 lg:row-start-1">
          {reordering ? (
            <ReorderList
              entries={exercises}
              saving={reorder.isPending}
              onSave={(ids) => reorder.mutate(ids)}
              onCancel={() => setReordering(false)}
            />
          ) : (
            <>
              <ol className="space-y-2">
                {groupRows(exercises).map((row, number) => {
                  if (row.kind === "circuit") {
                    const rounds = roundsOf(row.items);
                    const steps = row.items.reduce(
                      (sum, item) =>
                        sum + Array.from({ length: rounds }, (_, r) => isStepDone(item, r + 1)).filter(Boolean).length,
                      0,
                    );
                    const complete = row.items.every((item) => isComplete(item));
                    const lit = !finished && nextIndex >= row.index && nextIndex < row.index + row.items.length;
                    return (
                      <li key={`circuit-${row.index}`}>
                        <button
                          type="button"
                          onClick={() => open(row.index)}
                          aria-current={lit ? "step" : undefined}
                          className={rowClass(complete, lit)}
                        >
                          <span
                            className="outline-num w-11 shrink-0 text-[1.9rem] leading-none"
                            style={ordinalStroke(complete, lit)}
                          >
                            {ordinal(number)}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="line-clamp-2 block text-[1.02rem] leading-snug font-semibold">
                              {row.name}
                            </span>
                            <span className="block truncate text-[0.84rem] text-ink-mute">
                              {rounds} tours · {row.items.map((item) => item.exercise.name).join(", ")}
                            </span>
                          </span>
                          <Progress done={steps} target={rounds * row.items.length} />
                        </button>
                      </li>
                    );
                  }

                  const { entry, index } = row;
                  const complete = isComplete(entry);
                  const lit = !finished && index === nextIndex;
                  const comparison = comparisons.get(entry.id) ?? null;
                  return (
                    <li key={entry.id}>
                      {/* Done in turns with the card above: said before the first
                      set, not learnt from the button that sends one away. */}
                      {pairedWithPrevious(exercises, index) && (
                        <p className="label -mt-1 mb-1 flex items-center gap-1.5 pl-[4.75rem]">
                          <ArrowUpDown size={12} aria-hidden /> en superset
                        </p>
                      )}
                      <button
                        type="button"
                        onClick={() => open(index)}
                        aria-current={lit ? "step" : undefined}
                        className={rowClass(complete, lit)}
                      >
                        <span
                          className="outline-num w-11 shrink-0 text-[1.9rem] leading-none"
                          style={ordinalStroke(complete, lit)}
                        >
                          {ordinal(number)}
                        </span>

                        <span className="min-w-0 flex-1">
                          <span
                            className="line-clamp-2 block text-[1.02rem] leading-snug font-semibold"
                            style={{ viewTransitionName: exerciseTitle(entry.id) }}
                          >
                            {entry.exercise.name}
                          </span>
                          {comparison ? (
                            <CompareLines comparison={comparison} />
                          ) : (
                            <span className="num block text-[0.84rem] text-ink-mute">{describe(entry)}</span>
                          )}
                        </span>

                        {entry.lastTime && !finished && (
                          <span className="num hidden shrink-0 text-right text-[0.8rem] text-ink-mute sm:block">
                            {format(entry.lastTime.weightKg)} kg
                            <span className="block text-[0.72rem]">
                              {entry.lastTime.source === "sheet" ? "feuille" : "dernière fois"}
                            </span>
                          </span>
                        )}

                        <Progress done={setsDoneOn(entry)} target={entry.targetSets ?? 0} />
                      </button>
                    </li>
                  );
                })}
              </ol>

              <Link
                to={`/seance/${session.id}/ajouter`}
                className="mt-3 block rounded-card border border-dashed border-line px-4 py-3.5 text-center text-[0.9rem] text-ink-mute hover:text-ink-soft active:border-ink-mute active:text-ink"
              >
                Ajouter un mouvement
              </Link>

              {blocksOf(exercises).length > 1 && (
                <button
                  type="button"
                  onClick={() => setReordering(true)}
                  className="mt-2 flex h-11 w-full items-center justify-center text-[0.85rem] text-ink-mute hover:text-ink-soft active:text-ink"
                >
                  Changer l'ordre
                </button>
              )}

              <Link
                to="/choisir"
                className="mt-2 flex h-11 items-center justify-center text-[0.85rem] text-ink-mute hover:text-ink-soft active:text-ink"
              >
                Changer de séance
              </Link>

              {/* Stopping early is allowed, never by accident: down here with the
              other ways out, not where the eye lands above the list. */}
              {session.status !== "done" && nextIndex >= 0 && doneCount > 0 && (
                <button
                  type="button"
                  disabled={finish.isPending}
                  onClick={() => {
                    const left = exercises.length - doneCount;
                    const message = `${left} mouvement${left > 1 ? "s" : ""} pas fait${left > 1 ? "s" : ""}. Terminer quand même ?`;
                    if (window.confirm(message)) finish.mutate(session.id);
                  }}
                  className="block h-11 w-full text-center text-[0.85rem] text-ink-mute hover:text-ink-soft active:text-ink disabled:opacity-50"
                >
                  Terminer la séance maintenant
                </button>
              )}

              <button
                type="button"
                onClick={() => setConfirmingCancel(true)}
                className="block h-11 w-full text-center text-[0.85rem] text-ink-mute active:text-stop"
              >
                Annuler cette séance
              </button>

              <CancelSessionDialog
                open={confirmingCancel}
                title={`${session.name} · ${formatDate(session.date)}`}
                {...summary}
                pending={cancel.isPending}
                onConfirm={() => cancel.mutate({ sessionId: session.id, sets: summary.sets })}
                onClose={() => setConfirmingCancel(false)}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * A card of the list. The next movement is lit in the session's colour: the
 * way in, where the eye already is, in place of a card that repeated it.
 */
function rowClass(complete: boolean, lit: boolean): string {
  return [
    "card flex w-full items-center gap-4 px-4 py-3.5 text-left transition-colors active:bg-surface-high",
    lit ? "border-hue/70 bg-hue/10" : "hover:border-line",
    complete ? "opacity-70" : "",
  ].join(" ");
}

function ordinalStroke(complete: boolean, lit: boolean) {
  if (lit) return { WebkitTextStrokeColor: "var(--color-hue)" };
  return complete ? { WebkitTextStrokeColor: "var(--color-ink)" } : undefined;
}

function Trend({ value, label, className }: { value: number; label: string; className: string }) {
  return (
    <div>
      <dd className={["display text-[1.5rem] font-bold", value > 0 ? className : "text-ink-mute"].join(" ")}>
        {value}
      </dd>
      <dt className="label mt-1">{label}</dt>
    </div>
  );
}

/**
 * Today's sets over the last time's, set N against set N, then the gap.
 * Kilos once in the header rather than in every cell: with drop sets and
 * myo-rep clusters, a unit per number is what made it unreadable.
 */
function CompareLines({ comparison }: { comparison: Comparison }) {
  const { trend, gap, beforeDate, rows } = comparison;
  const Icon = trend === "up" ? ArrowUp : trend === "down" ? ArrowDown : Equal;
  // A set done one side at a time is two lines, each as long as a drop set
  // gets: next to the time before, on a phone, they broke after every figure.
  // There the time before goes under today's instead, and the lines keep whole.
  const sided = rows.some((row) => [row.today, row.before].some((cell) => cell !== null && typeof cell !== "string"));
  const under = sided ? "max-md:col-start-2" : "";
  return (
    <span className="mt-1.5 block">
      <span
        className={[
          "num grid gap-x-2 gap-y-0.5 text-[0.82rem]",
          sided
            ? "grid-cols-[auto_minmax(0,1fr)] md:grid-cols-[4.5rem_minmax(0,1fr)_minmax(0,1fr)]"
            : "grid-cols-[4.5rem_minmax(0,1fr)_minmax(0,1fr)]",
        ].join(" ")}
      >
        <span className="label">{sided ? <span className="max-md:hidden">kg × reps</span> : "kg × reps"}</span>
        <span className="label">
          {sided && <span className="md:hidden">kg × reps · </span>}
          Ce jour
        </span>
        <span className={["label", under].join(" ")}>{beforeDate ? shortDate(beforeDate) : ""}</span>
        {rows.map((row) => (
          <Fragment key={row.label}>
            <span className="text-ink-mute">{row.label}</span>
            <span
              className={[
                "font-semibold",
                row.trend === "up" ? "text-gain" : row.trend === "down" ? "text-iron" : "text-ink",
              ].join(" ")}
            >
              <CompareCellText cell={row.today} />
            </span>
            {/* Under today's, a dash for "no set then" is a line of nothing. */}
            <span className={["text-ink-mute", under, sided && row.before === null ? "max-md:hidden" : ""].join(" ")}>
              <CompareCellText cell={row.before} />
            </span>
          </Fragment>
        ))}
      </span>
      <span
        className={[
          "mt-1.5 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[0.76rem] font-bold",
          trend === "up"
            ? "bg-gain/12 text-gain"
            : trend === "down"
              ? "bg-iron/15 text-iron"
              : "bg-surface-high text-ink-soft",
        ].join(" ")}
      >
        {gap === null ? (
          "première fois"
        ) : (
          <>
            <Icon size={12} strokeWidth={3} aria-hidden />
            {formatGap(gap)}
          </>
        )}
      </span>
    </span>
  );
}

/** One line, or a line a side under the side's mark; a dash when the set was not done. */
function CompareCellText({ cell }: { cell: CompareCell | null }) {
  if (cell === null) return "—";
  if (typeof cell === "string") return cell;
  return SIDES.map((side) => (
    <span key={side} className="flex items-center gap-1.5 whitespace-nowrap">
      <SideMark side={side} size={8} /> {cell[side]}
    </span>
  ));
}

/** "29 sept.": the time before, as short as the column it sits in. */
function shortDate(iso: string): string {
  return new Date(`${iso}T12:00:00`).toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
}

/* ------------------------------------------------------------------ */

/** Sets done against sets prescribed, as a ring rather than a fraction. */
function Progress({ done, target }: { done: number; target: number }) {
  const complete = target > 0 && done >= target;
  const ratio = target > 0 ? Math.min(1, done / target) : done > 0 ? 1 : 0;
  const size = 34;
  const radius = 14;
  const circumference = 2 * Math.PI * radius;

  return (
    <span className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="var(--color-surface-high)" strokeWidth="3" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--color-ink)"
          strokeOpacity={complete ? 1 : 0.4}
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - ratio)}
          className="transition-[stroke-dashoffset] duration-500"
        />
      </svg>
      <span
        className={[
          "num absolute inset-0 flex items-center justify-center text-[0.8rem] font-bold",
          complete ? "text-ink" : "text-ink-soft",
        ].join(" ")}
      >
        {/* Nothing in the ring before the first set: a dash in a circle read as "remove". */}
        {complete ? "✓" : done || ""}
      </span>
    </span>
  );
}
